import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, Loader2, Dumbbell, Utensils, Calendar, 
  Trophy, ArrowRight, Sparkles, Heart, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchApi } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import confetti from 'canvas-confetti';

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateUser, user } = useAuth();
  const [generating, setGenerating] = useState(true);
  const [error, setError] = useState(null);
  const [programsReady, setProgramsReady] = useState({ training: false, nutrition: false });
  
  // Check if user came from challenge registration
  const isChallenge = user?.challenge_participant || searchParams.get('challenge') === 'true';

  useEffect(() => {
    generatePrograms();
  }, []);

  // Trigger confetti when programs are ready
  useEffect(() => {
    if (!generating && !error) {
      // Fire confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#CCFF00', '#10B981', '#F59E0B', '#8B5CF6']
      });
    }
  }, [generating, error]);

  const generatePrograms = async () => {
    try {
      // Update user subscription status
      updateUser({ subscription_status: 'trialing' });
      
      // Generate personalized programs now that they've subscribed
      try {
        await fetchApi('/programs/generate', { method: 'POST' });
        setProgramsReady(prev => ({ ...prev, training: true }));
      } catch (e) {
        console.error('Training generation error:', e);
      }
      
      try {
        await fetchApi('/nutrition/generate', { method: 'POST' });
        setProgramsReady(prev => ({ ...prev, nutrition: true }));
      } catch (e) {
        console.error('Nutrition generation error:', e);
      }
      
      setGenerating(false);
    } catch (err) {
      console.error('Generation error:', err);
      setError('Programs will be generated when you visit your dashboard.');
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-lg"
        >
          <div className="w-24 h-24 rounded-full bg-[#CCFF00]/20 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-12 h-12 text-[#CCFF00] animate-spin" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mb-4 text-white">Welcome to VYTA!</h1>
          <p className="text-lg text-zinc-400 mb-6">
            Your free trial has started. We're building your personalized programs...
          </p>
          
          <div className="space-y-3 max-w-xs mx-auto">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${programsReady.training ? 'bg-[#10B981]/20' : 'bg-zinc-700'}`}>
                {programsReady.training ? (
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                ) : (
                  <Dumbbell className="w-5 h-5 text-zinc-400 animate-pulse" />
                )}
              </div>
              <span className="text-sm text-zinc-300">Creating your training plan...</span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${programsReady.nutrition ? 'bg-[#10B981]/20' : 'bg-zinc-700'}`}>
                {programsReady.nutrition ? (
                  <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                ) : (
                  <Utensils className="w-5 h-5 text-zinc-400 animate-pulse" />
                )}
              </div>
              <span className="text-sm text-zinc-300">Preparing your meal plan...</span>
            </div>
          </div>
          
          <p className="text-xs text-zinc-500 mt-6">
            This may take a moment as we customize everything for you.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black text-lg">V</span>
            </div>
            <span className="font-bold text-lg">VYTA</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Success Icon */}
          <div className="w-24 h-24 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-14 h-14 text-[#10B981]" />
          </div>
          
          {/* Main Message */}
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            Thank You! 🎉
          </h1>
          <p className="text-xl text-zinc-400 mb-2">
            Your subscription is now active.
          </p>
          <p className="text-lg text-[#CCFF00] font-semibold mb-8">
            Your 7-day free trial has started!
          </p>

          {/* Challenge Registration Confirmation */}
          {isChallenge && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-yellow-400/10 via-amber-500/10 to-yellow-400/10 border border-yellow-400/30"
            >
              <div className="flex items-center justify-center gap-3 mb-3">
                <Trophy className="w-8 h-8 text-yellow-400" />
                <h2 className="text-xl font-bold text-yellow-400">Challenge Registered!</h2>
              </div>
              <p className="text-zinc-300">
                You're officially entered in the <strong>5 Week Global Transformation Challenge</strong>. 
                Start logging your progress to compete for USD $2,000!
              </p>
            </motion.div>
          )}

          {/* What's Ready Section */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="p-5 rounded-2xl bg-zinc-800/50 border border-zinc-700 text-left"
            >
              <div className="w-12 h-12 rounded-xl bg-[#CCFF00]/20 flex items-center justify-center mb-3">
                <Dumbbell className="w-6 h-6 text-[#CCFF00]" />
              </div>
              <h3 className="font-bold mb-1">Training Plan Ready</h3>
              <p className="text-sm text-zinc-400">Your personalized workout program is waiting for you.</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="p-5 rounded-2xl bg-zinc-800/50 border border-zinc-700 text-left"
            >
              <div className="w-12 h-12 rounded-xl bg-[#14B8A6]/20 flex items-center justify-center mb-3">
                <Utensils className="w-6 h-6 text-[#14B8A6]" />
              </div>
              <h3 className="font-bold mb-1">Meal Plan Ready</h3>
              <p className="text-sm text-zinc-400">Custom nutrition with recipes and shopping lists.</p>
            </motion.div>
          </div>

          {/* What's Next Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-8 p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800"
          >
            <h3 className="font-bold text-lg mb-4 flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5 text-[#CCFF00]" />
              What's Next?
            </h3>
            <div className="space-y-3 text-left">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#CCFF00] text-black font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
                <p className="text-zinc-300">Check out your <strong>personalized training program</strong> and start your first workout</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#CCFF00] text-black font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
                <p className="text-zinc-300">Review your <strong>meal plan</strong> and download your shopping list</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#CCFF00] text-black font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">3</div>
                <p className="text-zinc-300">Log your <strong>progress</strong> to track your transformation</p>
              </div>
            </div>
          </motion.div>

          {/* Trial Info */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mb-8 p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/50 flex items-center gap-3 justify-center"
          >
            <Clock className="w-5 h-5 text-zinc-400" />
            <p className="text-sm text-zinc-400">
              Your card will be charged after 7 days. Cancel anytime from your profile.
            </p>
          </motion.div>

          {error && (
            <p className="text-sm text-amber-400 mb-6">{error}</p>
          )}

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-10 py-6 text-lg"
              data-testid="go-dashboard-btn"
            >
              Go to My Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          {/* Support Note */}
          <p className="text-xs text-zinc-500 mt-8">
            Questions? Contact us at support@vyta-fit.com
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="w-4 h-4 text-red-500" />
            <span className="text-sm text-zinc-400">Thank you for choosing VYTA</span>
          </div>
          <p className="text-xs text-zinc-500">© {new Date().getFullYear()} VYTA. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
